import React, { useState } from 'react';
import { bolusnotesApi } from '../services/api';
import { useProfile } from '../contexts/ProfileContext';
import './AddBolusEntryModal.css';

interface AddBolusEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  initialEntry?: any;
}

const AddBolusEntryModal: React.FC<AddBolusEntryModalProps> = ({ isOpen, onClose, onSave, initialEntry }) => {
  const { selectedProfile } = useProfile();
  const [inputText, setInputText] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [insulin, setInsulin] = useState(initialEntry?.insulin_units?.toString() || '');
  const [carbs, setCarbs] = useState(initialEntry?.carbs_consumed?.toString() || '');
  const [glucose, setGlucose] = useState(initialEntry?.blood_glucose_before?.toString() || '');
  const [mealType, setMealType] = useState(initialEntry?.meal_type || '');
  const [injectionSite, setInjectionSite] = useState(initialEntry?.injection_site || '');
  const [notes, setNotes] = useState(initialEntry?.food_description || '');
  const [timestamp, setTimestamp] = useState(
    initialEntry?.timestamp 
      ? new Date(typeof initialEntry.timestamp === 'number' ? initialEntry.timestamp * 1000 : initialEntry.timestamp).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16)
  );

  const handleAnalyze = async () => {
    if (!inputText.trim()) return;
    setIsAnalyzing(true);
    try {
      const result = await bolusnotesApi.analyzeIntent(inputText);
      if (result.bolus !== undefined) setInsulin(result.bolus.toString());
      if (result.carbs !== undefined) setCarbs(result.carbs.toString());
      if (result.glucose !== undefined) setGlucose(result.glucose.toString());
      if (result.notes) setNotes(result.notes);
      if (result.type) setMealType(result.type);
    } catch (err) {
      console.error('Analysis failed:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const entryData = {
      timestamp: new Date(timestamp).toISOString(),
      insulinUnits: parseFloat(insulin),
      carbsConsumed: carbs ? parseFloat(carbs) : null,
      bloodGlucoseBefore: glucose ? parseFloat(glucose) : null,
      mealType,
      injectionSite,
      foodDescription: notes,
      profileId: selectedProfile?.id
    };

    try {
      if (initialEntry?.id) {
        await bolusnotesApi.entries.update(initialEntry.id, entryData);
      } else {
        await bolusnotesApi.entries.create(entryData);
      }
      onSave();
      onClose();
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{initialEntry ? 'Edit Bolus Entry' : 'Add Bolus Entry'}</h2>
          <button className="btn-close" onClick={onClose}>&times;</button>
        </div>

        {!initialEntry && (
          <div className="ai-assist-section">
            <textarea
              placeholder="e.g. '5 units for 45g carbs...'"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              className="ai-textarea"
            />
            <button 
              type="button" 
              className="btn-analyze" 
              onClick={handleAnalyze}
              disabled={isAnalyzing || !inputText.trim()}
            >
              {isAnalyzing ? '⌛ Analyzing...' : '🧠 AI Auto-Fill'}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="entry-form">
          <div className="form-group">
            <label>Time</label>
            <input 
              type="datetime-local" 
              value={timestamp}
              onChange={(e) => setTimestamp(e.target.value)}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Insulin (Units)</label>
              <input 
                type="number" 
                step="0.1" 
                placeholder="0.0"
                value={insulin}
                onChange={(e) => setInsulin(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Carbs (Grams)</label>
              <input 
                type="number" 
                step="1" 
                placeholder="0"
                value={carbs}
                onChange={(e) => setCarbs(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Blood Glucose (mg/dL)</label>
            <input 
              type="number" 
              step="1" 
              placeholder="100"
              value={glucose}
              onChange={(e) => setGlucose(e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Meal Type</label>
              <select value={mealType} onChange={(e) => setMealType(e.target.value)}>
                <option value="">None</option>
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <div className="form-group">
              <label>Injection Site</label>
              <select value={injectionSite} onChange={(e) => setInjectionSite(e.target.value)}>
                <option value="">None</option>
                <option value="arm">Arm</option>
                <option value="leg">Leg</option>
                <option value="abdomen">Abdomen</option>
                <option value="pump">Pump Site</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Notes / Food Description</label>
            <textarea 
              placeholder="What did you eat?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-save">Save Entry</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddBolusEntryModal;
